import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { type NotificationUnreadCountDto, useNotificationApi } from "@/api/notifications";
import { getAccessTokenProvider } from "@/auth/access-token-provider";
import { useSession } from "@/auth/session-provider";
import { GLOBAL_CONFIG } from "@/global-config";
import { getAcceptLanguage } from "@/locales/i18n";
import { useI18n } from "@/locales/i18n-provider";
import { markAllNotificationsReadInCache, markNotificationReadInCache, mergeIncomingNotifications, notificationQueryKeys } from "./notification-query";
import { useWebPush, type WebPushController } from "./use-web-push";

type NotificationContextValue = {
  unreadCount: number;
  unreadCountPending: boolean;
  realtimeNotificationSequence: number;
  refresh: () => Promise<unknown>;
  markRead: (cursor: number) => Promise<void>;
  markAllRead: (throughCursor?: number) => Promise<void>;
  webPush: WebPushController;
};

type NotificationAvailableSignal = {
  maxCursor: number;
};

const NotificationContext = createContext<NotificationContextValue | null>(null);
const CHANGES_PAGE_SIZE = 100;
const CONNECTION_RETRY_DELAY = 5_000;

function cursorStorageKey(agentId: string) {
  return `tickbird:notifications:last-cursor:${agentId}`;
}

function readStoredCursor(key: string) {
  const cursor = Number.parseInt(window.localStorage.getItem(key) ?? "0", 10);
  return Number.isSafeInteger(cursor) && cursor > 0 ? cursor : 0;
}

function consoleHubUrl() {
  const url = new URL(GLOBAL_CONFIG.apiBaseUrl, window.location.origin);
  url.pathname = `${url.pathname.replace(/\/api\/admin\/?$/, "").replace(/\/$/, "")}/hub/console`;
  url.search = "";
  url.hash = "";
  return url.toString();
}

export function useNotifications() {
  const value = useContext(NotificationContext);
  if (!value) {
    throw new Error("useNotifications 必须在 NotificationProvider 之内使用。");
  }
  return value;
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  const { agent } = useSession();
  const api = useNotificationApi();
  const webPush = useWebPush(agent.id);
  const queryClient = useQueryClient();
  const [realtimeNotificationSequence, setRealtimeNotificationSequence] = useState(0);
  const unreadQuery = useQuery({
    queryKey: notificationQueryKeys.unreadCount,
    queryFn: ({ signal }) => api.getUnreadCount(signal),
    refetchInterval: 60_000,
  });

  const refresh = useCallback(
    async () => Promise.all([queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists() }), unreadQuery.refetch()]),
    [queryClient, unreadQuery.refetch],
  );

  const markRead = useCallback(
    async (cursor: number) => {
      const previousCount = queryClient.getQueryData<NotificationUnreadCountDto>(notificationQueryKeys.unreadCount)?.count ?? 0;
      const readTime = new Date().toISOString();
      markNotificationReadInCache(queryClient, cursor, readTime);
      queryClient.setQueryData<NotificationUnreadCountDto>(notificationQueryKeys.unreadCount, { count: Math.max(0, previousCount - 1) });

      try {
        await api.markRead(cursor);
        void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.unreadCount });
      } catch (error) {
        await refresh();
        throw error;
      }
    },
    [api, queryClient, refresh],
  );

  const markAllRead = useCallback(
    async (throughCursor?: number) => {
      const result = await api.markAllRead(throughCursor);
      markAllNotificationsReadInCache(queryClient, result.throughCursor, new Date().toISOString());
      queryClient.setQueryData<NotificationUnreadCountDto>(notificationQueryKeys.unreadCount, { count: result.unreadCount });
    },
    [api, queryClient],
  );

  useEffect(() => {
    const storageKey = cursorStorageKey(agent.id);
    let lastCursor = readStoredCursor(storageKey);
    let stopped = false;
    let retryTimer: number | undefined;
    let syncPromise: Promise<void> | null = null;
    let syncRequested = false;
    let latestAnnouncedCursor = lastCursor;

    const syncChanges = async () => {
      syncRequested = true;
      if (syncPromise) {
        return syncPromise;
      }

      syncPromise = (async () => {
        while (syncRequested && !stopped) {
          syncRequested = false;
          let receivedCount = 0;

          do {
            const result = await api.getChanges({ afterCursor: lastCursor, limit: CHANGES_PAGE_SIZE });
            if (stopped) {
              return;
            }

            mergeIncomingNotifications(queryClient, result.items);
            receivedCount += result.items.length;
            lastCursor = result.nextCursor;
            window.localStorage.setItem(storageKey, String(lastCursor));

            if (!result.hasMore) {
              break;
            }
          } while (!stopped);

          if (receivedCount > 0) {
            await queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists() });
            const unread = await api.getUnreadCount();
            queryClient.setQueryData(notificationQueryKeys.unreadCount, unread);
          }
        }
      })()
        .catch((error) => {
          console.warn("通知补拉失败，将在下次连接或提示时重试。", error);
        })
        .finally(() => {
          syncPromise = null;
        });

      return syncPromise;
    };

    const connection = new HubConnectionBuilder()
      .withUrl(consoleHubUrl(), {
        // 每次协商与重连都会调用，向共享 Provider 取即可自然拿到最新令牌（嵌入设计 §9.4、AR-25）。
        accessTokenFactory: async () => (await getAccessTokenProvider().getAccessToken()) ?? "",
        headers: { "Accept-Language": getAcceptLanguage(locale) },
      })
      .withAutomaticReconnect([0, 2_000, 10_000, 30_000])
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on("notification.available", (signal: NotificationAvailableSignal) => {
      console.debug("收到通知可用信号：", signal, "当前 lastCursor:", lastCursor, "latestAnnouncedCursor:", latestAnnouncedCursor);
      if (signal.maxCursor > lastCursor) {
        if (signal.maxCursor > latestAnnouncedCursor) {
          latestAnnouncedCursor = signal.maxCursor;
          setRealtimeNotificationSequence((sequence) => sequence + 1);
        }
        void syncChanges();
      }
    });
    connection.onreconnected(() => {
      void syncChanges();
    });

    const scheduleStart = (delay: number) => {
      if (stopped || retryTimer !== undefined) {
        return;
      }

      retryTimer = window.setTimeout(() => {
        retryTimer = undefined;
        void start();
      }, delay);
    };

    const start = async () => {
      if (stopped || connection.state !== HubConnectionState.Disconnected) {
        return;
      }

      try {
        await connection.start();
        if (!stopped) {
          await syncChanges();
        }
      } catch (error) {
        if (!stopped) {
          console.warn("通知实时连接失败，稍后重试。", error);
          scheduleStart(CONNECTION_RETRY_DELAY);
        }
      }
    };

    connection.onclose(() => {
      scheduleStart(CONNECTION_RETRY_DELAY);
    });

    // 开发环境 StrictMode 会立即执行一次 setup → cleanup → setup。
    // 延迟到下一轮事件循环再连接，让第一次试运行在发起 negotiate 前就能被取消。
    scheduleStart(0);

    return () => {
      stopped = true;
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
      void connection.stop();
    };
  }, [agent.id, api, locale, queryClient]);

  const value = useMemo<NotificationContextValue>(
    () => ({
      unreadCount: unreadQuery.data?.count ?? 0,
      unreadCountPending: unreadQuery.isPending,
      realtimeNotificationSequence,
      refresh,
      markRead,
      markAllRead,
      webPush,
    }),
    [markAllRead, markRead, realtimeNotificationSequence, refresh, unreadQuery.data?.count, unreadQuery.isPending, webPush],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}
